import React, { useEffect, useRef, useState } from 'react'
import awsLogo from './assets/aws-logo.png'
import bedrockLogo from './assets/bedrock-logo.png'
import assessmentReport from './assets/screens/assessment-report.png'
import checkYourPet from './assets/screens/check-your-pet.png'
import editPet from './assets/screens/edit-pet.png'
import appleStoreButton from './assets/apple.store.button.png'
import googlePlayButton from './assets/google.play.button.png'
import androidLogo from './assets/android.png'
import iosLogo from './assets/ios.png'
import appLogo from './assets/logo.white.tr.png'
import heroGraphic from './assets/header-dog-and-cat.png'
import fdaVetLogo from './assets/source-logos/fda-vet.png'
import pmcLogo from './assets/source-logos/pmc.png'
import wsavaLogo from './assets/source-logos/wsava-logo.png'
import videoIcon from './assets/feature-icons/icon2.png'
import describeIcon from './assets/feature-icons/icon3.png'
import assessIcon from './assets/feature-icons/icon4.png'
import reportIcon from './assets/feature-icons/icon5.png'
import eyeIcon from './assets/feature-icons/icon6.png'
import warningIcon from './assets/feature-icons/icon8.png'
import knowledgeIcon from './assets/feature-icons/icon9.png'
import {
  getLandingAnalyticsConsent,
  initializeFirebaseAnalytics,
  setLandingAnalyticsConsent,
  trackLandingEvent,
  type LandingAnalyticsConsent
} from './firebase.analytics'

const screenshots = [
  {
    src: checkYourPet,
    alt: 'PawSight check your pet screenshot'
  },
  {
    src: assessmentReport,
    alt: 'PawSight assessment report screenshot'
  },
  {
    src: editPet,
    alt: 'PawSight edit pet screenshot'
  }
]

const steps = [
  {
    icon: describeIcon,
    title: 'Describe',
    description: 'Add the symptoms you are noticing and the context that matters.'
  },
  {
    icon: videoIcon,
    title: 'Capture',
    description: 'Record a short video or take a clear photo of the affected area.'
  },
  {
    icon: assessIcon,
    title: 'Assess',
    description: 'PawSight uses AI to compare your inputs with veterinary data and trusted veterinary knowledge sources.'
  },
  {
    icon: reportIcon,
    title: 'Act',
    description: 'Read the report and decide whether to monitor or seek care.'
  }
]

const faqs = [
  {
    icon: warningIcon,
    question: 'Is PawSight a replacement for a veterinarian?',
    answer:
      'No. PawSight is an informational tool only. It does not provide veterinary diagnoses, treatment, or medical advice.'
  },
  {
    icon: eyeIcon,
    question: 'What can PawSight help with?',
    answer:
      'PawSight helps pet owners document visible symptoms, review possible concerns, and better understand what next steps may be appropriate.'
  },
  {
    icon: knowledgeIcon,
    question: 'What type of input does PawSight use?',
    answer: 'PawSight can use photos, videos, and written symptom descriptions to generate an assessment.'
  }
]

const storeOptions = [
  {
    isIos: true,
    platform: 'iPhone and iPad',
    buttonAlt: 'Download on the App Store'
  },
  {
    isIos: false,
    platform: 'Android',
    buttonAlt: 'Get it on Google Play'
  }
]

const knowledgeSources = [
  {
    name: 'PubMed Central',
    href: 'https://www.ncbi.nlm.nih.gov/pmc/',
    logo: pmcLogo
  },
  {
    name: 'FDA Animal & Veterinary',
    href: 'https://www.fda.gov/animal-veterinary',
    logo: fdaVetLogo
  },
  {
    name: 'WSAVA',
    href: 'https://wsava.org/',
    logo: wsavaLogo
  }
]

export default function App() {
  const appStoreLink = 'https://apps.apple.com/us/app/pawsight-pet-insights/id6760793195'
  const googlePlayLink = 'https://play.google.com/store/apps/details?id=pet.pawsight.app&hl=en_US'
  const [activeScreenshot, setActiveScreenshot] = useState(0)
  const [analyticsConsent, setAnalyticsConsentState] = useState<LandingAnalyticsConsent>(null)
  const [analyticsPreferencesOpen, setAnalyticsPreferencesOpen] = useState(false)
  const pageViewTrackedRef = useRef(false)

  useEffect(() => {
    const storedConsent = getLandingAnalyticsConsent()
    setAnalyticsConsentState(storedConsent)

    if (storedConsent === true && !pageViewTrackedRef.current) {
      pageViewTrackedRef.current = true
      void initializeFirebaseAnalytics().then(() => (
        trackLandingEvent('landing_page_view', { page: 'home' })
      ))
    }

    const intervalId = window.setInterval(() => {
      setActiveScreenshot((current) => (current + 1) % screenshots.length)
    }, 2600)

    return () => window.clearInterval(intervalId)
  }, [])

  const handleAnalyticsConsent = async (enabled: boolean) => {
    await setLandingAnalyticsConsent(enabled)
    setAnalyticsConsentState(enabled)
    setAnalyticsPreferencesOpen(false)

    if (enabled && !pageViewTrackedRef.current) {
      pageViewTrackedRef.current = true
      await trackLandingEvent('landing_page_view', { page: 'home' })
    }
  }

  /**
   * Tracks navigation clicks for landing-page section links.
   */
  const handleNavigationClick = (section: string) => {
    void trackLandingEvent('landing_navigation_click', { section })
  }

  /**
   * Tracks App Store click-through as a dedicated Apple funnel event.
   */
  const handleAppStoreClick = () => {
    void trackLandingEvent('landing_app_store_click')
  }

  /**
   * Tracks Google Play click-through as a dedicated Android funnel event.
   */
  const handleGooglePlayClick = () => {
    void trackLandingEvent('landing_google_play_click')
  }

  /**
   * Tracks footer legal and partner link clicks.
   */
  const handleFooterLinkClick = (target: string) => {
    void trackLandingEvent('landing_footer_link_click', { target })
  }

  return (
    <div className='landing-shell'>
      <div className='hero-backdrop' aria-hidden='true'>
        <img src={heroGraphic} alt='' className='hero-pets-graphic' />
      </div>

      <header className='top-strip'>
        <div className='top-strip-inner'>
          <nav className='floating-nav'>
            <a href={'#about'} onClick={() => handleNavigationClick('about')}>About</a>
            <a href={'#how-it-works'} onClick={() => handleNavigationClick('how_it_works')}>How it works</a>
            <a href={'#download'} onClick={() => handleNavigationClick('download')}>Download</a>
            <a href={'#faq'} onClick={() => handleNavigationClick('faq')}>FAQ</a>
          </nav>
        </div>
      </header>

      <main className='main-wrap'>
        <section className='hero-panel'>
          <div className='hero-copy'>
            <img src={appLogo} alt='PawSight™' className='hero-logo' />
            <h1 className='hero-title'>
              Smart care - <span>Happier pets.</span>
            </h1>
            <p className='hero-text'>
              Describe the symptoms you see and, when helpful, add a photo or short video. PawSight
              highlights possible concerns and helps you understand when to contact a veterinarian.
            </p>

            <div className='hero-beta-actions'>
              <span className='hero-beta-label'>Available now</span>
              <div className='hero-store-row'>
                <a
                  href={appStoreLink}
                  target='_blank'
                  rel='noopener noreferrer'
                  onClick={handleAppStoreClick}
                >
                  <img src={appleStoreButton} alt='Download on the App Store' />
                </a>
                <a
                  href={googlePlayLink}
                  target='_blank'
                  rel='noopener noreferrer'
                  onClick={handleGooglePlayClick}
                >
                  <img src={googlePlayButton} alt='Get it on Google Play' />
                </a>
              </div>
            </div>
          </div>

          <div className='hero-visual'>
            <div className='hero-art-board' aria-label='PawSight app preview'>
              <div className='phone-frame'>
                <div className='phone-screen'>
                  <img
                    key={screenshots[activeScreenshot].src}
                    src={screenshots[activeScreenshot].src}
                    alt={screenshots[activeScreenshot].alt}
                    className='phone-carousel-image'
                  />
                </div>
              </div>
            </div>
          </div>

          <div id='how-it-works' className='hero-step-list'>
            <h2 className='hero-steps-title'>
              <img src={knowledgeIcon} alt='' aria-hidden='true' />
              <span>How it works</span>
            </h2>
            {steps.map(({ icon, title, description }, index) => (
              <article key={title} className='hero-step'>
                <div className='hero-step-number'>{index + 1}</div>
                <div className='hero-step-icon'>
                  <img src={icon} alt='' aria-hidden='true' />
                </div>
                <div className='hero-step-copy'>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section id='download' className='beta-section'>

          <div className='beta-guide'>
            <div className='beta-guide-heading'>
              <span className='section-label'>Download PawSight™</span>
              <h2>Get PawSight™ on your phone</h2>
            </div>

            <div className='beta-grid'>
              {storeOptions.map((item) => {
                const isIos = item.isIos
                const link = isIos ? appStoreLink : googlePlayLink
                const onClick = isIos ? handleAppStoreClick : handleGooglePlayClick

                return (
                  <article key={item.platform} className='beta-card'>
                    <div className='beta-card-top'>
                      <span
                        className={`platform-logo ${isIos ? 'platform-logo-ios' : 'platform-logo-android'}`}
                        aria-hidden='true'
                      >
                        <img src={isIos ? iosLogo : androidLogo} alt='' />
                      </span>
                      <span>{item.platform}</span>
                    </div>
                    <p className='store-description'>
                      Download PawSight from the {isIos ? 'App Store' : 'Google Play'}.
                    </p>
                    <a
                      href={link}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='beta-store-action'
                      onClick={onClick}
                    >
                      <span className='beta-store-badge'>
                        <img
                          src={isIos ? appleStoreButton : googlePlayButton}
                          alt={item.buttonAlt}
                        />
                      </span>
                    </a>
                  </article>
                )
              })}
            </div>
          </div>
        </section>

        <section id='about' className='content-section about-section'>
          <div className='about-heading'>
            <span className='section-label'>About PawSight™</span>
            <h2>Built for those uncertain moments before you know whether to worry</h2>
          </div>
          <div className='about-details'>
            <p>
              <strong>PawSight</strong> brings the symptoms you notice, relevant context, and optional
              photos or videos together into one clear assessment experience.
            </p>
            <p>
              The report highlights possible concerns, explains what may be worth monitoring, and helps
              you understand when contacting a veterinarian may be appropriate.
            </p>
            <p>
              Where information is available, <strong>PawSight</strong> may also help you understand the
              approximate cost range you could expect for relevant veterinary care. Future versions are
              planned to help surface more affordable veterinary care alternatives.
            </p>
          </div>
        </section>

        <section id='faq' className='content-section faq-section'>
          <div className='section-heading'>
            <span className='section-label'>FAQ</span>
            <h2>What people usually want to know first</h2>
          </div>

          <div className='faq-grid'>
            {faqs.map((faq) => (
              <article key={faq.question} className='faq-card'>
                <img src={faq.icon} alt='' aria-hidden='true' className='faq-icon' />
                <h3>{faq.question}</h3>
                <p>{faq.answer}</p>
              </article>
            ))}
          </div>
        </section>

        <section className='bottom-panel'>
          <div className='bottom-panel-inner'>
            <div className='bottom-copy'>
              <span className='section-label'>Important</span>
              <p>
                PawSight uses artificial intelligence and is designed to support awareness, not replace a
                licensed veterinarian. If your pet is in distress or may require urgent treatment, contact
                an emergency veterinary clinic immediately.
              </p>
            </div>

            <div className='bottom-links'>
              <a href='/terms.html' onClick={() => handleFooterLinkClick('terms')}>Terms & Conditions</a>
              <a href='/privacy.html' onClick={() => handleFooterLinkClick('privacy')}>Privacy Statement</a>
              <a href='/sources.html' onClick={() => handleFooterLinkClick('sources')}>Knowledge Sources</a>
              <button type='button' onClick={() => setAnalyticsPreferencesOpen(true)}>Analytics preferences</button>
            </div>

            <div className='powered-row' aria-label='Knowledge sources and technology partners'>
              <div className='footer-logo-group'>
                <span className='footer-logo-label'>Veterinary knowledge sources</span>
                <div className='footer-logo-group-row'>
                  {knowledgeSources.map((source) => (
                    <a
                      key={source.name}
                      href={source.href}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='footer-logo-link'
                      onClick={() => handleFooterLinkClick(`source_${source.name.toLowerCase().replaceAll(' ', '_')}`)}
                    >
                      <img src={source.logo} alt={source.name} className='footer-source-logo' />
                    </a>
                  ))}
                </div>
              </div>

              <div className='footer-logo-group footer-tech-group'>
                <span className='footer-logo-label'>Technology</span>
                <div className='footer-logo-group-row'>
                  <a
                    href='https://aws.amazon.com'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='footer-logo-link'
                    onClick={() => handleFooterLinkClick('aws')}
                  >
                    <img src={awsLogo} alt='AWS' className='partner-logo' />
                  </a>
                  <a
                    href='https://aws.amazon.com/bedrock/'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='footer-logo-link'
                    onClick={() => handleFooterLinkClick('bedrock')}
                  >
                    <img src={bedrockLogo} alt='Amazon Bedrock' className='partner-logo bedrock-logo' />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className='site-footer'>
        © {new Date().getFullYear()} PawSight™. All rights reserved. Operated by{' '}
        <a
          href='https://evalua.studio'
          target='_blank'
          rel='noopener noreferrer'
          onClick={() => handleFooterLinkClick('evaluate_studio')}
        >
          Evalua Studio LLC
        </a>
        .
      </footer>

      {(analyticsConsent === null || analyticsPreferencesOpen) ? (
        <aside
          className='analytics-consent'
          role='dialog'
          aria-labelledby='analytics-consent-title'
          aria-describedby='analytics-consent-description'>
          <div className='analytics-consent-copy'>
            <h2 id='analytics-consent-title'>Optional analytics</h2>
            <p id='analytics-consent-description'>
              Help us understand how people use PawSight and improve the experience.
              We collect page and button interactions, along with basic browser and device information.
              You can change your choice at any time.{' '}
              <a href='/privacy.html'>Privacy Notice</a>
            </p>
          </div>
          <div className='analytics-consent-actions'>
            <button type='button' onClick={() => void handleAnalyticsConsent(false)}>
              Use without analytics
            </button>
            <button type='button' onClick={() => void handleAnalyticsConsent(true)}>
              Allow analytics
            </button>
          </div>
        </aside>
      ) : null}
    </div>
  )
}
